import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { captureLead } from "@/lib/leadCapture";
import { leadMessage, redirectToWhatsApp } from "@/lib/whatsapp";
import { CheckCircle2, MessageCircle } from "lucide-react";

// Reusable lead capture form (name, phone, email, message) backed by the
// gnc-lead-form capture endpoint. Includes a hidden honeypot field for
// basic bot protection.
export default function LeadCaptureForm({ testId = "lead-capture-form", compact = false, onSuccess }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", _gotcha: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [waUrl, setWaUrl] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form._gotcha) return; // honeypot triggered, silently ignore
    setLoading(true);
    const chatText = leadMessage("Hi GrabNChat, I'd like to know more.", [
      ["Name", form.name],
      ["Phone", form.phone],
      ["Email", form.email],
      ["Message", form.message],
    ]);
    try {
      await captureLead(form);
      setSuccess(true);
      setWaUrl(redirectToWhatsApp(chatText));
      toast.success("Thanks! Opening WhatsApp…");
      onSuccess?.();
    } catch (err) {
      if (err?.kind === "validation") {
        toast.error(err.message);
      } else {
        // Our side failed — don't lose the lead: continue on WhatsApp.
        setSuccess(true);
        setWaUrl(redirectToWhatsApp(chatText));
        toast.message("Opening WhatsApp to send your message…");
        onSuccess?.();
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8" data-testid={`${testId}-success`}>
        <div className="mx-auto w-14 h-14 rounded-full bg-[#25D366]/10 grid place-items-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-[#25D366]" />
        </div>
        <h3 className="font-display text-xl font-bold text-slate-900">Thanks for reaching out!</h3>
        <p className="text-slate-600 mt-2">Opening WhatsApp so you can chat with us on +91 9311 57 2747.</p>
        {waUrl && (
          <a
            href={waUrl}
            className="inline-flex items-center gap-2 mt-4 px-5 h-10 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold text-sm"
            data-testid={`${testId}-whatsapp-btn`}
          >
            <MessageCircle className="w-4 h-4" /> Continue on WhatsApp
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={testId}>
      {!compact && <h2 className="font-display text-2xl font-bold text-slate-900">Send us a message</h2>}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="lead-name">Your name *</Label>
          <Input id="lead-name" required value={form.name} onChange={update("name")} data-testid={`${testId}-name`} />
        </div>
        <div>
          <Label htmlFor="lead-phone">Phone number</Label>
          <Input id="lead-phone" type="tel" value={form.phone} onChange={update("phone")} data-testid={`${testId}-phone`} />
        </div>
      </div>
      <div>
        <Label htmlFor="lead-email">Email</Label>
        <Input id="lead-email" type="email" value={form.email} onChange={update("email")} data-testid={`${testId}-email`} />
      </div>
      <div>
        <Label htmlFor="lead-message">How can we help?</Label>
        <Textarea id="lead-message" rows={compact ? 3 : 4} value={form.message} onChange={update("message")} data-testid={`${testId}-message`} />
      </div>
      {/* Honeypot: hidden from real users, left empty; bots tend to fill every field */}
      <input
        type="text"
        name="_gotcha"
        value={form._gotcha}
        onChange={update("_gotcha")}
        style={{ display: "none" }}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full h-11 font-semibold"
        data-testid={`${testId}-submit-btn`}
      >
        {loading ? "Sending..." : "Send"}
      </Button>
    </form>
  );
}
